import { CardHeader, Grid, TextField, Typography, useMediaQuery, Divider, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { userManagementActions } from '../../store/reducers/user-management';
import Styles from '../../theme/commonCss/Styles';
import FormModal from '../../common/formModal';
import { generatePassword } from '../../utils/form-validations';
import { userFormSchema, userFormUISchema } from './form';
import Ajv from 'ajv';
import AjvErrors from 'ajv-errors';
import { get, reduce } from 'lodash';
import { useOutletContext } from 'react-router-dom';

import SelectComponent from '../../components/Select';

const AddEditUser = ({
  isOpen,
  userData,
  setUserData,
  selectedRole,
  setSelectedRole,
  isEdit,
  handleClose = () => {},
  setUserDetails,
  rolesData,
}: any) => {
  const themeData: any = useTheme();
  const [userRoleList, setUserRoleList] = useState(null);

  const dispatch = useDispatch();

  const responsiveSize = useMediaQuery('(max-width:900px)');

  const ajv = new Ajv({ allErrors: true });
  AjvErrors(ajv);
  const validate = ajv.compile(userFormSchema);
  const [ajvErrors, setAjvErrors] = useState<any>({});

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setUserData({ ...userData, [name]: value });
  };

  const handleRoleChange = (e: any) => {
    const selectedId = e?.target.value;
    setSelectedRole(selectedId);
  };

  useEffect(() => {
    const userRoleObject = rolesData.find((item: any) => item?.id === selectedRole);
    setUserRoleList(userRoleObject);
  }, [selectedRole]);

  const handleFormClose = () => {
    handleClose();
    setAjvErrors({});
  };

  const handleSubmit = (event: any) => {
    event.preventDefault();

    const reqParams: any = {
      id: userData.id,
      email: userData.email,
      username: userData.email,
      firstName: userData.firstName,
      lastName: userData.lastName,
      roles: [userRoleList],
    };

    const trimmedServiceData = reduce(
      reqParams,
      (acc: any, value: any, key: any) => {
        acc[key] = typeof value === 'string' ? value.trim() : value;
        return {
          ...acc,
        };
      },
      {}
    );

    if (validate({ ...trimmedServiceData })) {
      const params: any = {
        id: reqParams.id,
        email: reqParams.email,
        username: reqParams.email,
        first_name: reqParams.firstName,
        last_name: reqParams.lastName,
        roles: reqParams.roles,
      };

      const { email, username, ...updateParams } = params;

      if (isEdit) {
        dispatch(userManagementActions.updateUserRequest(updateParams));
      } else {
        const password = generatePassword();
        setUserDetails({ username: userData.email, password: password });
        dispatch(
          userManagementActions.addUserRequest({ ...params, credentials: [{ type: 'password', value: password }] })
        );
      }
      handleClose();
      setAjvErrors({});
    } else {
      if (validate.errors) {
        setAjvErrors(validate.errors[0]);
      }
    }
  };

  return (
    <FormModal
      title={isEdit ? 'EDIT USER' : 'ADD NEW USER'}
      isOpen={isOpen}
      handleSubmit={handleSubmit}
      handleClose={handleFormClose}
    >
      <CardHeader
        title='User Details'
        titleTypographyProps={Styles.title}
        sx={{marginBottom: '10px' }}
      />
      <Divider />
      <Grid container spacing={2} sx={{ ...Styles.gridContainer, flexDirection: 'column', marginLeft: 0 }}>
        {userFormUISchema
          .filter((item: any) => !isEdit || (isEdit && item.name !== 'email'))
          .map(({ label, name, type, options, required }: any) => {
            if (type === 'input') {
              return (
                <Grid container key={name} sx={{ display: 'flex', alignItems: 'center', marginBlock: '12px' }}>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={4}
                    sx={{
                      display: 'flex',
                      justifyContent: responsiveSize ? 'flex-start' : 'flex-end',
                      pb: responsiveSize ? '4px' : 0,
                    }}
                  >
                    <Typography
                      sx={{
                        ...Styles.inputLabel, color: themeData.palette.text.primary,
                        pb: get(ajvErrors, 'instancePath', '').includes(name) ? '20px' : '0',
                      }}
                      className={required ? 'required-field' : ''}
                    >
                      {label}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={12} md={8}>
                    <TextField
                      type='text'
                      margin='normal'
                      fullWidth
                      id={name}
                      onChange={handleChange}
                      name={name}
                      autoComplete={name}
                      variant='outlined'
                      value={userData[name]}
                      size='small'
                      sx={{ margin: 0 }}
                      helperText={get(ajvErrors, 'instancePath', '').includes(name) && ajvErrors.message}
                      error={get(ajvErrors, 'instancePath', '').includes(name)}
                    />
                  </Grid>
                </Grid>
              );
            } else if (type === 'select') {
              return (
                <Grid container key={name} sx={{ display: 'flex', alignItems: 'flex-start', marginBlock: '12px' }}>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={4}
                    sx={{
                      display: 'flex',
                      justifyContent: responsiveSize ? 'flex-start' : 'flex-end',
                      pb: responsiveSize ? '4px' : 0,
                      pt: responsiveSize ? '0px' : '10px',
                    }}
                  >
                    <Typography sx={{...Styles.inputLabel, color: themeData.palette.text.primary}} className={required ? 'required-field' : ''}>
                      {label}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={12} md={8}>
                    <SelectComponent
                      value={selectedRole}
                      options={rolesData.map((role: any) => ({ ...role, label: role.name, value: role.id }))}
                      onChange={handleRoleChange}
                      name={name}
                      error={get(ajvErrors, 'instancePath', '').includes(name)}
                      helperText='Please select Role'
                    />
                  </Grid>
                </Grid>
              );
            } else {
              return <></>;
            }
          })}
      </Grid>
    </FormModal>
  );
};

export default AddEditUser;
